/* eslint-disable react/require-default-props */

import React from 'react';
import { Box, Button, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { RuleImpact } from '../../../shared';
import { useInvertedLocalize } from '../../../utils/localization';

import Breadcrumbs from './Breadcrumbs/Breadcrumbs';
import invertedMessages from './messages';
import { type ImpactLevel } from './RuleImpactIndicator';

import './RuleDetailsHeader.css';

interface RuleDetailsHeaderProps {
  title: string; // main page title
  subtitle?: string;
  icon?: React.ReactNode;
  showBreadcrumbs?: boolean; // whether to show breadcrumbs nav
  impactLevel?: ImpactLevel;
  className?: string;
}

/**
 * Header component for rule details page containing title, subtitle, breadcrumbs, and impact indicator
 *
 * @param props - The component props
 * @returns JSX element containing the rule details header
 */
function RuleDetailsHeader({
  title,
  subtitle,
  icon,
  showBreadcrumbs = false,
  impactLevel,
  className,
}: RuleDetailsHeaderProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className={cx('Rules-RuleDetailsHeader', className)}>
      {showBreadcrumbs && (
        <Box className='Rules-RuleDetailsHeader-breadcrumbs'>
          <Breadcrumbs />
        </Box>
      )}

      <Box tag='header' className='Rules-RuleDetailsHeader-content'>
        <Box className='Rules-RuleDetailsHeader-topRow'>
          <Box className='Rules-RuleDetailsHeader-titleSection'>
            {icon && (
              <Box className='Rules-RuleDetailsHeader-icon'>
                {React.isValidElement(icon) && icon}
              </Box>
            )}

            <Box className='Rules-RuleDetailsHeader-titleRow'>
              <Typography
                kind='h1'
                color='--color-foreground-neutral-bold'
                className='Rules-RuleDetailsHeader-title'>
                {title}
              </Typography>
              {impactLevel && (
                <RuleImpact impactLevel={impactLevel} iconSize='small' />
              )}
            </Box>
          </Box>

          {/* Export button: MV2+ */}
          <Box className='Rules-RuleDetailsHeader-actions'>
            <Box className='Rules-RuleDetailsHeader-export'>
              <Button leftIcon={<Icon kind='Export' size='small' />}>
                {l('Export')}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Subtitle: aligned with left edge */}
        {subtitle && (
          <Typography
            kind='body2'
            color='--color-foreground-neutral-subtle'
            className='Rules-RuleDetailsHeader-subtitle'>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default RuleDetailsHeader;
